import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { HotelContext } from "../../context/HotelContext";
import { getLocale, translate } from "../../utils/Translation";
import CalendarIcon from "../../img/v2/calendar-icon.svg";
import "./MonthYearNavigator.css";

const clampMonthYear = (month, year) => {
  let m = month;
  let y = year;
  if (m < 1) {
    m = 12;
    y -= 1;
  } else if (m > 12) {
    m = 1;
    y += 1;
  }
  return { month: m, year: y };
};

const MonthYearNavigator = ({ month, year, onChange, yearsBack = 10 }) => {
  const { lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const now = useMemo(() => new Date(), []);

  const formatMonthYear = (m, y) => {
    const monthLabel = new Date(2024, m - 1, 1).toLocaleDateString(locale, { month: "long" });
    const capitalizedMonth = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
    return `${capitalizedMonth} ${y}`;
  };

  const months = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: new Date(2024, i, 1).toLocaleDateString(locale, { month: "long" }),
      })),
    [locale]
  );

  const years = useMemo(() => {
    const baseYear = now.getFullYear();
    return Array.from({ length: yearsBack }, (_, i) => {
      const y = baseYear - i;
      return { value: y, label: y.toString() };
    });
  }, [now, yearsBack]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const shiftMonth = (delta) => {
    const next = clampMonthYear(month + delta, year);
    onChange(next);
  };

  const handleMonthSelect = (e) => {
    const nextMonth = parseInt(e.target.value, 10);
    onChange({ month: nextMonth, year });
  };

  const handleYearSelect = (e) => {
    const nextYear = parseInt(e.target.value, 10);
    onChange({ month, year: nextYear });
  };

  return (
    <div className="month-year-nav" aria-label={translate("v2_month_nav_aria", lang)}>
      <button
        type="button"
        className="month-year-nav__arrow"
        onClick={() => shiftMonth(-1)}
        aria-label={translate("v2_month_prev", lang)}
      >
        ◀
      </button>

      <div className="month-year-selector" ref={dropdownRef}>
        <button
          className="month-year-selector__button"
          onClick={() => setIsOpen(!isOpen)}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          <img src={CalendarIcon} alt="" className="month-year-selector__icon" />
          <span className="month-year-selector__text">{formatMonthYear(month, year)}</span>
        </button>

        {isOpen && (
          <div className="month-year-selector__dropdown">
            <div className="month-year-selector__dropdown-row">
              <div className="month-year-selector__dropdown-section">
                <label className="month-year-selector__label">{translate("v2_month_label", lang)}</label>
                <select className="month-year-selector__select" value={month} onChange={handleMonthSelect}>
                  {months.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label.charAt(0).toUpperCase() + m.label.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="month-year-selector__dropdown-section">
                <label className="month-year-selector__label">{translate("v2_year_label", lang)}</label>
                <select className="month-year-selector__select" value={year} onChange={handleYearSelect}>
                  {years.map((y) => (
                    <option key={y.value} value={y.value}>
                      {y.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        className="month-year-nav__arrow"
        onClick={() => shiftMonth(1)}
        aria-label={translate("v2_month_next", lang)}
      >
        ▶
      </button>
    </div>
  );
};

export default MonthYearNavigator;
